import React from 'react'

import { useFormContext } from 'react-hook-form'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { useReactHookFormExternalSubmit } from '@contexts/ReactHookFormExternalSubmitProvider'

import type { ButtonWithIconProps } from '@components/ui/Buttons'
import { AsyncButtonWithIcon, Button } from '@components/ui/Buttons'

import { DetailsResetMessage, FormResetConfirmDialog } from './elements'

import { ButtonsContainerDiv } from './styles'

const messages = defineMessages({
  saveButtonText: {
    id: 'form.button.save',
    defaultMessage: 'Mentés',
  },
  savedButtonText: {
    id: 'form.button.saved',
    defaultMessage: 'Elmentve',
  },
})

function scrollToFirstError(selector = '[data-form-control-error="true"]') {
  window.setTimeout(() => {
    const el = document.querySelector<HTMLElement>(selector)
    if (el) {
      window.scrollTo({ top: el.offsetTop, behavior: 'smooth' })
    }
  })
}

interface ReactHookFormSubmitButtonProps<InitialValues extends object>
  extends Omit<ButtonWithIconProps, 'children' | 'type' | 'variant'> {
  buttonClassName?: string
  buttonText?: StringOrMessage
  confirmDialogMessage?: StringOrMessage
  disabledUntilDirty?: boolean
  disabledUntilValid?: boolean
  initialValues?: InitialValues
  isCreateOnly?: boolean
  isCreateAndAddNewFlowRef?: React.MutableRefObject<boolean>
  lastUpdated?: Nullable<string>
  needConfirmOnReset?: boolean
  onSaveAndAddNewButtonHandler?: VoidFunction
  variant?: 'primaryText' | 'primaryContained' | 'secondaryContained'
}

export function ReactHookFormSubmitButton<InitialValues extends object>({
  buttonClassName,
  buttonText: customButtonText,
  className,
  confirmDialogMessage,
  disabled = false,
  disabledUntilDirty = false,
  disabledUntilValid = false,
  initialValues,
  isCreateOnly = false,
  isCreateAndAddNewFlowRef,
  lastUpdated,
  needConfirmOnReset = false,
  onSaveAndAddNewButtonHandler,
  variant = 'primaryContained',
  ...rest
}: ReactHookFormSubmitButtonProps<InitialValues>) {
  const [open, setOpen] = React.useState(false)
  const [isSaveAndAddNewFlow, setIsSaveAndAddNewFlow] = React.useState(Boolean(isCreateAndAddNewFlowRef?.current))
  const { formatMessage, formatDate, formatTime } = useIntl()
  const {
    reset,
    formState: { isDirty, isSubmitting, isSubmitted, isSubmitSuccessful, errors, isValid },
  } = useFormContext()
  const triggerSubmit = useReactHookFormExternalSubmit()

  // scroll to first error
  React.useEffect(() => {
    if (!isSubmitting && isSubmitted && !isSubmitSuccessful && Object.keys(errors).length > 0) {
      scrollToFirstError()
    }
  }, [errors, isSubmitSuccessful, isSubmitted, isSubmitting])

  React.useEffect(() => {
    if (isCreateAndAddNewFlowRef?.current && !isSubmitting && isSubmitted && isSubmitSuccessful) {
      setIsSaveAndAddNewFlow(false)
    }
  }, [isCreateAndAddNewFlowRef, isSubmitSuccessful, isSubmitted, isSubmitting])

  function handleResetRequest() {
    if (needConfirmOnReset) {
      setOpen(true)
    } else {
      handleReset()
    }
  }

  function handleClose() {
    setOpen(false)
  }

  function handleReset() {
    reset(initialValues)
    if (open) {
      setOpen(false)
    }
  }

  function handleSubmitAndAddNew() {
    if (isCreateAndAddNewFlowRef) {
      setIsSaveAndAddNewFlow(true)
      isCreateAndAddNewFlowRef.current = true
    }
    triggerSubmit()
  }

  // default save
  let buttonText = formatMessage(messages.saveButtonText)
  if (!isCreateOnly && !isDirty) {
    // saved
    buttonText = formatMessage(messages.savedButtonText)
    if (lastUpdated) {
      // saved with date and time stamp
      buttonText = `${formatMessage(messages.savedButtonText)}: ${formatDate(lastUpdated)} ${formatTime(lastUpdated)}`
    }
  }

  let isSubmitDisabled = false
  if (disabledUntilValid) {
    isSubmitDisabled = !isValid
  } else if (disabledUntilDirty) {
    isSubmitDisabled = !isDirty
  }

  return (
    <ButtonsContainerDiv className={className}>
      {!isCreateOnly && isDirty && initialValues && (
        <>
          <Button variant="primaryText" onClick={handleResetRequest}>
            {DetailsResetMessage}
          </Button>
          <FormResetConfirmDialog
            descriptionMessage={confirmDialogMessage}
            open={open}
            onClose={handleClose}
            onConfirm={handleReset}
          />
        </>
      )}
      <AsyncButtonWithIcon
        {...rest}
        className={buttonClassName}
        disabled={disabled || isSubmitting || isSubmitDisabled}
        loading={!isSaveAndAddNewFlow && isSubmitting}
        type="submit"
        variant={isSubmitDisabled ? 'secondaryContained' : variant}
      >
        {customButtonText || buttonText}
      </AsyncButtonWithIcon>
      {isCreateAndAddNewFlowRef && isCreateOnly && (
        <AsyncButtonWithIcon
          {...rest}
          className={buttonClassName}
          disabled={disabled || isSubmitting || isSubmitDisabled}
          loading={isSaveAndAddNewFlow && isSubmitting}
          onClick={handleSubmitAndAddNew}
          type="button"
          variant={isSubmitDisabled ? 'secondaryContained' : 'primaryText'}
        >
          <FormattedMessage id="form.button.saveAndAddNew" defaultMessage="Mentés és új hozzáadása" />
        </AsyncButtonWithIcon>
      )}
    </ButtonsContainerDiv>
  )
}
